import { useForm, SubmitHandler, Controller } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import * as E from '../elementor'
import React, { useState, useId, useEffect } from 'react'
import RulaButton from '@pathccm/rula-ui/button'
import { RulaTextField, RulaCheckbox } from '@pathccm/rula-ui/form'
import classNames from 'classnames'
import { freshpaintTrack } from '../freshpaint'
import { useFlags } from 'gatsby-plugin-launchdarkly'
import { EventPrefix, Trigger, UserType } from '@pathccm/common-analytics/events'
import { getReportingAdapter } from '../reporting/get-reporting-adapter'

interface Inputs {
  email: string
  acceptTerms: boolean
}

const EmailCaptureSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  acceptTerms: z.literal<boolean>(true, { errorMap: () => ({ message: 'Please agree to the conditions below' }) })
})

interface EmailCaptureProps {
  emailCaptureSection?: E.Section<true>
  userType?: UserType
  className?: string
  primaryHeader?: string
  secondaryHeader?: string
  downloadLink?: string
  downloadButtonText?: string
  isDownload?: boolean
}

const EmailCapture: React.FC<EmailCaptureProps> = ({
  emailCaptureSection,
  userType = UserType.PATIENT,
  className = '',
  primaryHeader = 'The care you need, when you need it',
  secondaryHeader = 'Learn how Rula can support your mental health journey',
  downloadLink = '',
  downloadButtonText = '',
  isDownload = false
}) => {
  const [emailTouched, setEmailTouched] = useState(false)
  const hideEmailCapture = emailCaptureSection?.cssClasses?.includes('hide-email-capture') ?? false
  const hideAcceptTerms = emailCaptureSection?.cssClasses?.includes('hide-accept-terms') ?? false
  const formName = emailCaptureSection?.cssId ?? 'email_capture'
  const productArea = 'Email Capture'
  let heading: E.HeadingWidget[] = []
  let image: E.ImageWidget[] = []
  let button: E.ButtonWidget[] = []
  const { marketingSiteSegmentTransition } = useFlags()
  const reporting = getReportingAdapter(marketingSiteSegmentTransition)

  const {
    handleSubmit,
    formState: {
      errors,
      isValid,
      submitCount,
      isSubmitted,
      isSubmitSuccessful
    },
    reset,
    control
  } = useForm<Inputs>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(EmailCaptureSchema),
    defaultValues: {
      email: '',
      acceptTerms: hideAcceptTerms
    }
  })

  // Track the download event in Amplitude
  const triggerDownloadEvent = async (email: string) => {
    if (marketingSiteSegmentTransition) {
      reporting.track({
        eventName: {
          prefix: EventPrefix.MARKETING_SITE,
          trigger: Trigger.CONTENT_DOWNLOAD
        },
        properties: {
          form_name: formName,
          download_triggered: true,
          product_area: 'Patient Download'
        },
        // Segment requires context containing the user email trait in order to propagate it to Iterable
        context: {
          traits: {
            email
          }
        }
      })
    }
  }

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    if (marketingSiteSegmentTransition) {
      reporting.identify({
        properties: {
          email: data.email,
          user_type: userType
        }
      })

      reporting.track({
        eventName: {
          prefix: EventPrefix.MARKETING_SITE,
          trigger: Trigger.EMAIL_CAPTURE
        },
        properties: {
          form_name: formName,
          email_given: data.email
        },
        // Segment requires context containing the user email trait in order to propagate it to Iterable
        context: {
          traits: {
            email: data.email
          }
        }
      })

      reporting.track({
        eventName: {
          prefix: EventPrefix.MARKETING_SITE,
          trigger: Trigger.FORM_SUBMITTED
        },
        properties: {
          form_name: formName,
          product_area: productArea
        }
      })

      if (userType === UserType.PATIENT) {
        freshpaintTrack('patient_email_capture')
      } else if (userType === UserType.PROVIDER) {
        freshpaintTrack('provider_email_capture')
      }
      if (!isDownload) {
        setTimeout(async () => {
          reset()
        }, 3000)
      }
    }
  }

  useEffect(() => {
    if (isSubmitted && !isValid && marketingSiteSegmentTransition) {
      reporting.track({
        eventName: {
          prefix: EventPrefix.MARKETING_SITE,
          trigger: Trigger.SUBMISSION_ERROR
        },
        properties: {
          form_name: formName,
          product_area: productArea,
          errors: Object.keys(errors).map((key) => errors[key].message)
        }
      })
    }
  }, [submitCount, marketingSiteSegmentTransition])

  useEffect(() => {
    if (emailTouched && marketingSiteSegmentTransition) {
      reporting.track({
        eventName: {
          prefix: EventPrefix.MARKETING_SITE,
          trigger: Trigger.FORM_INTERACTION
        },
        properties: {
          form_name: formName,
          field_name: 'email'
        }
      })
    }
  }, [emailTouched, marketingSiteSegmentTransition])

  // Render nothing if we specify via CSS class 'hide-email-capture'
  if (emailCaptureSection !== undefined && hideEmailCapture) {
    return <></>
  }

  if (emailCaptureSection !== undefined) {
    ({ heading, image, button } = E.collect(emailCaptureSection))
    primaryHeader = heading[0]?.title ?? primaryHeader
    secondaryHeader = heading[1]?.title ?? secondaryHeader
    downloadLink = button[0]?.link ?? ''
    downloadButtonText = button[0]?.text ?? 'Download PDF'
  }
  const id = useId()

  // Define buttonText with the required logic
  const buttonText = (!isSubmitSuccessful && !isDownload)
    ? 'Subscribe'
    : (isDownload && !isSubmitSuccessful) ? 'Submit' : ''

  return (
    <section data-testid='email-capture-component' className={['email-capture', emailCaptureSection?.cssClasses, className].join(' ')}>
      <div className='call-to-action-wrapper'>
        {(image[0] !== undefined) && (
          <img className='ec-image' data-testid='email-capture-image' src={image[0]?.image?.fileUrl} alt={image[0]?.image?.altText} />
        )}
        <h6 className='primary-header'>{(isDownload && isSubmitSuccessful) ? secondaryHeader : primaryHeader}</h6>
        {(secondaryHeader !== undefined && !isDownload) && (
          <p className='secondary-header' data-testid='secondary-header'>
            {secondaryHeader}
          </p>
        )}
      </div>
      {isSubmitSuccessful && isDownload
        ? (
          <a className='email-capture-link' href={downloadLink} target='_blank' rel='noopener noreferrer' download>
            <button
              className='email-capture-download-button'
              onClick={async () => await triggerDownloadEvent(control._formValues.email)}
            >
              {downloadButtonText}
            </button>
          </a>
          )
        : (
          <div />
          )}
      {!(isDownload && isSubmitSuccessful) && (
        <div className='email-form-wrapper'>
          <div className='email-input-col'>
            <form className='email-input-row'>
              <Controller
                name='email'
                control={control}
                render={({ field, fieldState: { invalid } }) => (
                  <RulaTextField
                    data-testid='email-input'
                    className='email-input'
                    placeholder='Enter your email'
                    aria-label='email'
                    onFocus={() => setEmailTouched(true)}
                    isInvalid={invalid}
                    {...field}
                  />
                )}
              />
              <RulaButton
                style='Primary'
                data-testid='subscribe-button'
                id='subscribe'
                type='button'
                isDisabled={isSubmitSuccessful}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onPress={async () => await handleSubmit(onSubmit)()}
                size='large'
              >
                {buttonText}
                <svg className={classNames({ 'checkmark-icon': true, animate: isSubmitSuccessful })} width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path className='check' d='M7.5 12L10.5 15L16.5 9' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                  <circle className='circle' cx='12' cy='12' fill='none' r='10' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
              </RulaButton>
            </form>

            {(errors.email != null) && (
              <div id='email-error' className='error-message' data-testid='email-error-message'>
                {errors.email.message}
              </div>
            )}
            {(errors.acceptTerms != null) && (errors.email == null) && (
              <div id='terms-error' className='error-message' data-testid='terms-error-message'>
                {errors.acceptTerms.message}
              </div>
            )}
            {!hideAcceptTerms &&
              <div className='accept-terms-row'>
                <Controller
                  control={control}
                  name='acceptTerms'
                  render={({ field: { onChange, value, name } }) => (
                    <RulaCheckbox
                      id={'acceptTerms-' + id}
                      style='Default'
                      className='rula-checkbox'
                      onChange={(v) => {
                        if (marketingSiteSegmentTransition) {
                          reporting.track({
                            eventName: {
                              prefix: EventPrefix.MARKETING_SITE,
                              trigger: Trigger.FORM_INTERACTION
                            },
                            properties: {
                              form_name: formName,
                              field_name: name
                            }
                          })
                        }
                        onChange(v)
                      }}
                      isSelected={value}
                    />
                  )}
                />
                <label htmlFor={'acceptTerms-' + id}>
                  I agree to receive emails from Rula and accept the terms outlined in <a href='https://www.rula.com/client-consent-to-email-phi-sms/'>Client Email Consent</a> and <a href='https://www.rula.com/privacy-policy/'>Privacy Policy</a>.
                </label>
              </div>}
          </div>
        </div>
      )}
    </section>
  )
}

export default EmailCapture
